import { Component, h, Prop, Host, State, Element, Watch } from "@stencil/core";
import { AutosuggestItem } from "./autosuggest-response";
import { getSuggestions } from "./get-suggestions";
import { getUniqueId } from "../../utils/unique-id";
import { escapeRegexCharacters } from "../../utils/escapeRegexCharacters";

@Component({
    tag: "ux-autosuggest-input",
    styleUrl: "autosuggest-input.scss",
    shadow: false,
})
export class AutosuggestInput {
    private inputElement: HTMLInputElement;
    private effectiveInputElement: HTMLInputElement;

    /** placeholder text for input */
    @Prop() placeholder?: string = "";
    /** name attribute for input */
    @Prop() name?: string = "";
    /** id attribute for input. A unique id will be generated if this is empty */
    @Prop() inputId?: string = "";
    /** label for input */
    @Prop() label?: string = "";
    /** additional info label for the the input field */
    @Prop() labelInfo: string = "";
    /** URL for autosuggest-API */
    @Prop() url?: string = "";
    /** indicates if the autosuggest input is disabled */
    @Prop() disabled: boolean = false;
    /** indicates if the autosuggest input is required  */
    @Prop() required: boolean = false;
    /** indicates if the autosuggest input has an error  */
    @Prop() error: boolean = false;
    /** Error message that is rendered if component has an error */
    @Prop() errorMsg: string | null = null;
    /** suggestions set statically, without an api request */
    @Prop() staticSuggestions: AutosuggestItem[] = [];

    @State() suggestions: AutosuggestItem[] = [];
    @State() input: string = "";
    @State() isHidden: boolean = true;
    @State() id: string = "";
    @State() hasStaticSuggestions: boolean = false;

    @Element() domNode: HTMLUxAutosuggestInputElement;

    @Watch("inputId")
    componentWillLoad() {
        const { inputId } = this;
        this.id = inputId || getUniqueId();
    }

    componentDidLoad() {
        this.setHasStaticSuggestionsState();
    }

    @Watch("staticSuggestions")
    setHasStaticSuggestionsState() {
        if (this.staticSuggestions.length === 0) return;
        const checkSuggestions = [...this.staticSuggestions];
        if (
            checkSuggestions.filter((staticSuggestion: AutosuggestItem) => {
                staticSuggestion.description !== undefined;
            }).length === 0
        ) {
            this.hasStaticSuggestions = true;
        }
    }

    private onInput(event: Event) {
        const { value } = event.target as HTMLInputElement;
        this.input = value;
        if (this.staticSuggestions.length) {
            const suggestionValue = this.getValueFromSuggestion(value);
            this.effectiveInputElement.value = suggestionValue || value;
            if (value === "") this.suggestions = this.staticSuggestions;
            this.suggestions = this.filterStaticSuggestions(value);
            return;
        }
        if (value === "" || this.url === "") {
            this.suggestions = [];
            return;
        }
        getSuggestions(`${this.url}?q=${value}`)
            .then(
                (response: AutosuggestItem[]) => (this.suggestions = response)
            )
            .catch((error) => console.error(error));
    }

    private filterStaticSuggestions = (value: string): AutosuggestItem[] => {
        return this.staticSuggestions.filter(
            (staticSuggestion: AutosuggestItem) =>
                (staticSuggestion.label || staticSuggestion.value)
                    .toLowerCase()
                    .indexOf(value.toLowerCase()) !== -1
        );
    };

    private getValueFromSuggestion = (value: string): string | null => {
        const suggestion = this.staticSuggestions.find(
            (suggestion) =>
                (suggestion.label.toLowerCase() ||
                    suggestion.value.toLowerCase()) ===
                value.toLowerCase().trim()
        );
        if (suggestion !== undefined) {
            return suggestion.value;
        } else return null;
    };

    private onBlur() {
        this.isHidden = true;
    }

    private onFocus() {
        this.isHidden = false;
        this.input = this.inputElement.value;
        if (this.staticSuggestions.length) {
            if (this.inputElement.value === "")
                this.suggestions = this.staticSuggestions;
            this.suggestions = this.filterStaticSuggestions(
                this.inputElement.value
            );
            return;
        }
    }

    private onSuggestionItemClick(event: Event, value: string): void {
        event.preventDefault();
        this.inputElement.value = value;
        if (this.hasStaticSuggestions) {
            const suggestionItem = this.staticSuggestions.find((suggestion) => {
                return suggestion.value === value;
            });
            this.inputElement.value = suggestionItem.label;
            this.effectiveInputElement.value = suggestionItem.value;
        }
        this.suggestions = [];
    }

    private getPatternAttribute = (): string | null => {
        if (!this.staticSuggestions.length) return null;
        return `^(${this.staticSuggestions
            .map((suggestion) => escapeRegexCharacters(suggestion.value))
            .join("|")})$`;
    };

    render() {
        const baseClass = "ux-autosuggest-input";
        const {
            placeholder,
            name,
            suggestions,
            id,
            input,
            label,
            labelInfo,
            isHidden,
            disabled,
            required,
            error,
            errorMsg,
            hasStaticSuggestions,
        } = this;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--error`]: error,
                }}
                onFocusout={() => this.onBlur()}
            >
                <ux-input-label
                    text={label}
                    inputId={id}
                    error={error}
                    errorMsg={errorMsg}
                    required={required}
                    labelInfo={labelInfo}
                />
                <div class={`${baseClass}__input-container`}>
                    {hasStaticSuggestions && (
                        <input
                            name={name}
                            type="text"
                            style={{ display: "none" }}
                            required={required}
                            pattern={this.getPatternAttribute()}
                            ref={(el) => (this.effectiveInputElement = el)}
                            aria-hidden="true"
                        />
                    )}
                    <input
                        class="ux-input"
                        name={hasStaticSuggestions ? null : name}
                        type="text"
                        placeholder={placeholder}
                        autocomplete="off"
                        autocapitalize="off"
                        id={id}
                        disabled={disabled}
                        required={hasStaticSuggestions ? null : required}
                        onInput={(event) => this.onInput(event)}
                        onFocus={() => this.onFocus()}
                        ref={(el) => (this.inputElement = el)}
                    />
                    <div
                        class={{
                            [`${baseClass}__suggestions`]: true,
                            [`${baseClass}__suggestions--hidden`]:
                                isHidden || this.suggestions.length === 0,
                            [`${baseClass}__suggestions--slim`]: this
                                .hasStaticSuggestions,
                        }}
                    >
                        {suggestions?.map(
                            ({
                                value,
                                description,
                                image,
                                label: suggestionLabel,
                            }) => (
                                <a
                                    class={`${baseClass}__suggestion-link`}
                                    href="#"
                                    onFocus={() => this.onFocus()}
                                    onClick={(event) =>
                                        this.onSuggestionItemClick(event, value)
                                    }
                                >
                                    <ux-autosuggest-item
                                        text={
                                            !hasStaticSuggestions
                                                ? value
                                                : suggestionLabel
                                        }
                                        input={input}
                                        subline={description}
                                        image={image}
                                        slim={hasStaticSuggestions}
                                    />
                                </a>
                            )
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
